import * as TogglePrimitive from '@radix-ui/react-toggle'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const toggleVariants = cva(
  'inline-flex items-center gap-3 rounded-xl text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-transparent justify-center disabled:opacity-50',
        outline:
          'border border-input bg-transparent hover:bg-accent hover:text-accent-foreground justify-center disabled:opacity-50',
        fintalk:
          'bg-white border border-[rgba(86,83,134,0.17)] hover:bg-[#f3f3ff] hover:border-[rgba(86,83,134,0.3)] data-[state=on]:bg-[#dad9f9] data-[state=on]:border-[#2f2a9c] data-[state=on]:hover:bg-[#dad9f9] disabled:bg-[#f3f3ff] disabled:border-[rgba(86,83,134,0.17)]',
      },
      size: {
        default: 'h-10 px-3 min-w-10',
        sm: 'h-9 px-2.5 min-w-9',
        lg: 'h-11 px-5 min-w-11',
        xl: 'h-12 p-2.5 w-max',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

const toggleIconVariants = cva(
  'inline-flex items-center justify-center rounded-md p-2 shrink-0',
  {
    variants: {
      variant: {
        default: '',
        outline: '',
        fintalk:
          'bg-[#f3f3ff] data-[state=on]:bg-gradient-to-b data-[state=on]:from-[#393760] data-[state=on]:to-[#5e619d] disabled:bg-[rgba(86,83,134,0.17)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const toggleTextVariants = cva('text-sm font-medium leading-5 shrink-0', {
  variants: {
    variant: {
      default: '',
      outline: '',
      fintalk:
        'text-[#7c8d9f] data-[state=on]:text-[#333333] disabled:text-[rgba(86,83,134,0.17)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

interface ToggleProps
  extends React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root>,
    VariantProps<typeof toggleVariants> {
  icon?: React.ReactNode
  text?: string
}

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  ToggleProps
>(({ className, variant, size, icon, text, children, ...props }, ref) => {
  if (variant === 'fintalk' && (icon || text)) {
    return (
      <TogglePrimitive.Root
        ref={ref}
        className={cn(toggleVariants({ variant, size, className }), 'group')}
        {...props}
      >
        {icon && (
          <div
            className={cn(
              toggleIconVariants({ variant }),
              'group-data-[state=on]:bg-gradient-to-b group-data-[state=on]:from-[#393760] group-data-[state=on]:to-[#5e619d]',
              'group-disabled:bg-[rgba(86,83,134,0.17)]',
              '[&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
              '[&_svg]:stroke-[#393760] [&_svg]:stroke-[1.5]',
              'group-data-[state=on]:[&_svg]:stroke-white',
              'group-disabled:[&_svg]:stroke-[#565386]',
            )}
          >
            {icon}
          </div>
        )}
        {text && (
          <p
            className={cn(
              toggleTextVariants({ variant }),
              'group-data-[state=on]:text-[#333333]',
              'group-disabled:text-[rgba(86,83,134,0.17)]',
            )}
          >
            {text}
          </p>
        )}
        {children}
      </TogglePrimitive.Root>
    )
  }

  return (
    <TogglePrimitive.Root
      ref={ref}
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    >
      {children}
    </TogglePrimitive.Root>
  )
})

Toggle.displayName = TogglePrimitive.Root.displayName

export { Toggle, toggleVariants, toggleIconVariants, toggleTextVariants }
